import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Link,
  Divider,
  IconButton,
} from '@mui/material';
import { Chat as ChatIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { signup, login, resetPassword, formatErrorCode } from '../config/auth';
import { useAppContext } from '../context/AppContext';
import { UserData } from '../types';
import LoginScene from '../components/login/LoginScene';
import { FloatingBubble } from '../components/login/Decorations';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'rgba(255,255,255,0.03)',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
    '&:hover fieldset': { borderColor: 'rgba(124,77,255,0.4)' },
    '&.Mui-focused fieldset': { borderColor: '#7c4dff' },
  },
  '& .MuiInputLabel-root': { color: '#8888a8' },
};

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { setUserData } = useAppContext();
  const [isSignUp, setIsSignUp] = useState(false);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      setError('Please enter your email address first.');
      return;
    }
    setResetLoading(true);
    setError('');
    setResetSent(false);
    try {
      await resetPassword(email);
      setResetSent(true);
    } catch (err: unknown) {
      const firebaseErr = err as { code?: string };
      setError(formatErrorCode(firebaseErr.code || ''));
    } finally {
      setResetLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isSignUp) {
        await signup(username, email, password);
        navigate('/profile');
      } else {
        const user = await login(email, password);
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const data = userDoc.data() as UserData | undefined;
        if (data) {
          setUserData(data);
          navigate(data.name ? '/chat' : '/profile');
        }
      }
    } catch (err: unknown) {
      const firebaseErr = err as { code?: string };
      setError(formatErrorCode(firebaseErr.code || ''));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        background:
          'radial-gradient(ellipse at 20% 50%, rgba(124,77,255,0.12) 0%, transparent 50%), ' +
          'radial-gradient(ellipse at 80% 20%, rgba(255,64,129,0.08) 0%, transparent 50%), ' +
          'radial-gradient(ellipse at 50% 90%, rgba(0,230,118,0.04) 0%, transparent 50%), ' +
          '#0a0a14',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Left branding panel */}
      <LoginScene />

      {/* Right form panel */}
      <Box
        sx={{
          flex: { xs: 1, md: '0 0 480px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <FloatingBubble size={6} top="8%" left="10%" color="rgba(124,77,255,0.3)" delay={0} duration={6} animation="float-slow" />
        <FloatingBubble size={4} top="15%" left="85%" color="rgba(255,64,129,0.3)" delay={1} duration={5} animation="float-medium" />

        <Paper
          elevation={0}
          sx={{
            p: 5,
            width: '100%',
            maxWidth: 420,
            borderRadius: 4,
            background: 'rgba(18,18,31,0.85)',
            backdropFilter: 'blur(24px)',
            border: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          {/* Mobile logo */}
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              justifyContent: 'center',
              alignItems: 'center',
              mb: 3,
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #7c4dff, #ff4081)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 0 20px rgba(124,77,255,0.3)',
              }}
            >
              <ChatIcon sx={{ fontSize: 22, color: 'white' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#e8e8f0' }}>
              ChatApp
            </Typography>
          </Box>

          <Typography variant="h5" sx={{ mb: 0.5, color: '#e8e8f0' }}>
            {isSignUp ? 'Create Account' : 'Welcome Back'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#6868a0', mb: 3 }}>
            {isSignUp
              ? 'Sign up to start chatting with your friends'
              : 'Sign in to continue your conversations'}
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                bgcolor: 'rgba(255,64,64,0.08)',
                border: '1px solid rgba(255,64,64,0.15)',
              }}
            >
              {error}
            </Alert>
          )}

          {resetSent && (
            <Alert
              severity="success"
              sx={{
                mb: 2,
                bgcolor: 'rgba(0,230,118,0.08)',
                border: '1px solid rgba(0,230,118,0.15)',
              }}
            >
              Password reset email sent! Check your inbox.
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            {isSignUp && (
              <TextField
                fullWidth
                label="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                sx={{ mb: 2, ...inputSx }}
              />
            )}
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              sx={{ mb: 2, ...inputSx }}
            />
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              slotProps={{
                input: {
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                      sx={{ color: '#5555a0' }}
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  ),
                },
              }}
              sx={{ mb: 1, ...inputSx }}
            />

            {!isSignUp && (
              <Box sx={{ textAlign: 'right', mb: 1 }}>
                <Link
                  component="button"
                  type="button"
                  variant="caption"
                  onClick={handleForgotPassword}
                  sx={{
                    color: '#7c4dff',
                    fontWeight: 500,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {resetLoading ? 'Sending...' : 'Forgot Password?'}
                </Link>
              </Box>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  size="small"
                  sx={{ color: '#5555a0', '&.Mui-checked': { color: '#7c4dff' } }}
                />
              }
              label={
                <Typography variant="caption" sx={{ color: '#6868a0' }}>
                  I agree to the Terms of Use & Privacy Policy
                </Typography>
              }
              sx={{ mb: 2 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !agreed}
              sx={{
                mb: 2,
                py: 1.5,
                background: 'linear-gradient(135deg, #7c4dff, #ff4081)',
                boxShadow: '0 4px 20px rgba(124,77,255,0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #6a3de8, #e8356f)',
                  boxShadow: '0 6px 28px rgba(124,77,255,0.45)',
                },
                '&.Mui-disabled': { background: 'rgba(124,77,255,0.2)' },
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : isSignUp ? (
                'Create Account'
              ) : (
                'Sign In'
              )}
            </Button>

            <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.06)' }} />

            <Typography
              variant="body2"
              sx={{ textAlign: 'center', color: '#6868a0' }}
            >
              {isSignUp ? 'Already have an account? ' : "Don't have an account? "}
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => { setIsSignUp(!isSignUp); setError(''); }}
                sx={{ fontWeight: 600, color: '#7c4dff' }}
              >
                {isSignUp ? 'Sign In' : 'Sign Up'}
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default Login;
